// replay-sfx.ts — the spectator's sound palette: which sim PlaySfx ids a REPLAY plays.
// The sim fires player-directed feedback through the same host seam ("structure ready",
// "vehicle ready", repair loops, the replicator's unit voice picks, storage/power warnings) —
// right for the player being addressed, wrong for a neutral observer. The replay keeps the
// diegetic battlefield only: weapon fire, impacts, damage, deaths, captures, and the
// replicator/activator machinery. Everything else stays silent.
import * as S from '../../../sim/src/data/soundeffects.ts';

const ALLOWED = new Set<number>([
  // combat — fire
  S.ksfxLightTankFire,
  S.ksfxMediumTankFire,
  S.ksfxMachineGunInfantryFire,
  S.ksfxMachineGunVehicleFire,
  S.ksfxMachineGunTowerFire,
  S.ksfxMachineGunTowerAttack,
  S.ksfxRocketInfantryFire,
  S.ksfxRocketVehicleFire,
  S.ksfxRocketTowerFire,
  S.ksfxRocketTowerAttack,
  S.ksfxArtilleryFire,
  S.ksfxAndyFire,
  // combat — impacts
  S.ksfxLightTankImpact,
  S.ksfxMediumTankImpact,
  S.ksfxRocketInfantryImpact,
  S.ksfxRocketVehicleImpact,
  S.ksfxRocketTowerImpact,
  S.ksfxArtilleryImpact,
  // combat — damage + destruction
  S.ksfxGalaxiteProcessorDamaged,
  S.ksfxGalaxiteProcessorDestroyed,
  S.ksfxGalaxiteWarehouseDamaged,
  S.ksfxGalaxiteWarehouseDestroyed,
  S.ksfxHeadquartersDamaged,
  S.ksfxHeadquartersDestroyed,
  S.ksfxHumanResourceCenterDamaged,
  S.ksfxHumanResourceCenterDestroyed,
  S.ksfxMachineGunTowerDamaged,
  S.ksfxMachineGunTowerDestroyed,
  S.ksfxRadarDamaged,
  S.ksfxRadarDestroyed,
  S.ksfxReactorDamaged,
  S.ksfxReactorDestroyed,
  S.ksfxResearchCenterDamaged,
  S.ksfxResearchCenterDestroyed,
  S.ksfxRocketTowerDamaged,
  S.ksfxRocketTowerDestroyed,
  S.ksfxVehicleTransportStationDamaged,
  S.ksfxVehicleTransportStationDestroyed,
  // (unit/hero death sounds arrive as `sfxc:` category strings below, never as numeric ids —
  // the sim's only numeric PlaySfx calls are the consts fields checked above)
  S.ksfxTakeoverSpecialistStructureCaptured,
  // the replicator + activator machinery
  S.ksfxReplicatorOn,
  S.ksfxReplicatorOff,
  S.ksfxReplicatorBuild,
  S.ksfxActivatorOn,
  S.ksfxActivatorOff,
]);

// `sfxc:<n>` category picks resolve INSIDE SoundMgr — allow only the death categories, the
// complete set the sim can emit through this seam (every unit's sfxcDestroyed is one of these
// four; select/move/attack voice categories arrive the same way from the replicator and stay
// blocked)
const ALLOWED_CATEGORIES = new Set<string>([
  `sfxc:${S.ksfxcInfantryDestroyed}`,
  `sfxc:${S.ksfxcVehicleDestroyed}`,
  `sfxc:${S.ksfxcAndyDestroyed}`,
  `sfxc:${S.ksfxcFoxDestroyed}`,
]);

export function spectatorSfxAllowed(sfx: number | string): boolean {
  return typeof sfx === 'number' ? ALLOWED.has(sfx) : ALLOWED_CATEGORIES.has(sfx);
}
